import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import useOutsideAlerter from '../hooks/useOutsideAlerter';
import { useChat } from '../modules/ChatPane';
import {
  deleteMessage,
  addMessageAction,
  addActionAllMessagesFromUserInChannel,
  kickUserFromSession,
  banUserFromSession,
  messageRoom,
} from '../helpers/pubNubFunctions';
import { FontAwesomeConfigurations } from '../../utils/Configuration/FontAwesomeConfiguration';

// Handles all message moderation functions and
// renders the dropdown to enact them
export default function MessageModeration({
  moderatedQAFlow = false,
  message,
  room,
  channeltype,
  isLocked = true,
  containerRef,
}) {
  const {
    context: { content, isModerator, isPlatformModerator },
  } = useChat();

  const [showModOptions, setShowModOptions] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);
  const [answer, setAnswer] = useState('');
  const [menuDirection, setMenuDirection] = useState('down');

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (wrapperRef && wrapperRef.current) {
      const { top, height } = wrapperRef.current.getBoundingClientRect();
      const containerHeight = containerRef.current.clientHeight;
      if (top + (height + 15) > containerHeight) {
        setMenuDirection('up');
      }
    }
  }, [wrapperRef]);

  useOutsideAlerter(wrapperRef, e => {
    setShowModOptions(false);
  });

  const onClickOpen = e => {
    setShowModOptions(old => !old);
  };

  const onDeleteMessage = e => {
    deleteMessage(room, message.timetoken);
  };

  const flagAllMessagesInRooms = () => {
    const rooms = [
      content.roomForChannelType(OTSessionContent.CHANNELTYPE_CHAT),
      content.roomForChannelType(OTSessionContent.CHANNELTYPE_QA),
      content.roomForChannelType(OTSessionContent.CHANNELTYPE_MOD),
    ];

    rooms.map(channel => {
      addActionAllMessagesFromUserInChannel(
        channel,
        message.message?.user?.user_id,
        PubNubCenter.ACTION_TYPE_FLAG,
        PubNubCenter.ACTION_KICKED,
      );
    });
  };

  const onKickUser = e => {
    flagAllMessagesInRooms();
    kickUserFromSession(message.message?.user?.user_id, room);
  };

  const onKickBanUser = e => {
    flagAllMessagesInRooms();
    banUserFromSession(message.message?.user?.user_id, message.message?.user?.chat_room, content.Id);
  };

  const onEndPoll = e => {
    addMessageAction(room, message.timetoken, {
      type: 'endedEarly',
      value: 'true',
    });
  };

  const onPostQuestion = e => {
    addMessageAction(room, message.timetoken, {
      type: 'posted',
      value: 'true',
    });
  };

  const onAskPresenter = e => {
    const aNewData = {
      data: {
        user: {
          user_id: message.message.user.user_id,
          user_firstname: message.message.user.user_firstname,
          user_lastname: message.message.user.user_lastname,
          user_avatar: message.message.user.avatar,
          user_icon: message.message.user.avatar,
        },
        message: message.message.text,
        originalTimetoken: message.timetoken,
      },
      //@ts-ignore
      type: PubNubPayload.TYPE_ASK_PRESENTER,
    };
    messageRoom(room, aNewData, () => {});
  };

  const onShowAddAnswer = e => {
    setIsAnswering(true);
  };

  const onAddAnswer = e => {
    addMessageAction(room, message.timetoken, {
      type: 'answered',
      value: 'true',
    });
    addMessageAction(room, message.timetoken, {
      type: 'qaAnswer',
      value: answer,
    });
    setAnswer('');
    setIsAnswering(false);
  };

  const onAddAnswerCancel = e => {
    setAnswer('');
    setIsAnswering(false);
  };

  const onMarkAsAnswered = e => {
    addMessageAction(room, message.timetoken, {
      type: 'answered',
      value: 'true',
    });
  };

  const dropdown = {
    hidden: {
      opacity: 0,
      transition: { duration: 0.15 },
      display: 'none',
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.15 },
      display: 'flex',
    },
  };

  return isModerator ? (
    <>
      <motion.div className={`polls_moderate ${showModOptions ? 'open' : ''}`} onClick={onClickOpen} ref={wrapperRef}>
        <div className={`moderate_icon`}>
          <i className={`${FontAwesomeConfigurations.ICON_ELLIPSIS_VERTICAL} fa-fw`} />
        </div>
        <motion.div
          className={`mod_dropdown`}
          variants={dropdown}
          initial={`hidden`}
          animate={showModOptions ? 'visible' : 'hidden'}
          style={{ y: 12, top: menuDirection === 'down' ? 12 : 'auto', bottom: menuDirection === 'up' ? 12 : 'auto' }}
        >
          <div className={`mod_dropdown_header`}>MESSAGE CONTROLS</div>
          {channeltype == 'poll' && !isLocked && (
            <div onClick={onEndPoll}>
              <div className={`icon`}>
                <i className={`${FontAwesomeConfigurations.ICON_CLOCK} fa-fw`} />
              </div>{' '}
              End Poll Early
            </div>
          )}

          {channeltype == 'qa' && (
            <>
              <div onClick={onAskPresenter}>
                <div className={`icon`}>
                  <i className={`${FontAwesomeConfigurations.ICON_MESSAGES_QUESTION_DUOTONE} fa-fw`} />
                </div>{' '}
                Ask Presenter
              </div>
              <div onClick={onShowAddAnswer}>
                <div className={`icon`}>
                  <i className={`${FontAwesomeConfigurations.ICON_PEN_CIRCLE_DUOTONE} fa-fw`} />
                </div>{' '}
                Add Answer
              </div>
              <div onClick={onMarkAsAnswered}>
                <div className={`icon`}>
                  <i className={`${FontAwesomeConfigurations.ICON_COMMENT_CHECK_DUOTONE} fa-fw`} />
                </div>{' '}
                Mark as Answered
              </div>
              {moderatedQAFlow ? (
                <>
                  <div onClick={onPostQuestion}>
                    <div className={`icon`}>
                      <i className={`${FontAwesomeConfigurations.ICON_THUMBTACK_DUOTONE} fa-fw`} />
                    </div>{' '}
                    Post Question
                  </div>
                </>
              ) : (
                <></>
              )}
            </>
          )}

          {isLocked && (
            <div onClick={onDeleteMessage}>
              <div className={`icon`}>
                <i className={`${FontAwesomeConfigurations.ICON_TRASH_DUOTONE} fa-fw`} />
              </div>{' '}
              Delete Message
            </div>
          )}

          {(channeltype == 'chat' || channeltype == 'qa') && (
            <>
              <div className={`mod_dropdown_header`}>USER CONTROLS</div>
              <div onClick={onKickUser}>
                <div className={`icon`}>
                  <i className={`${FontAwesomeConfigurations.ICON_PERSON_TO_DOOR_DUOTONE} fa-fw`} />
                </div>{' '}
                Kick User
              </div>
              {isPlatformModerator && (
                <div onClick={onKickBanUser}>
                  <div className={`icon`}>
                    <i className={`${FontAwesomeConfigurations.ICON_BAN} fa-fw`} />
                  </div>{' '}
                  Kick/Ban User
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
      {isAnswering && (
        <div className={`qa_add_answer`}>
          <textarea value={answer} onChange={e => setAnswer(e.target.value)} rows={5} />
          <div>
            <a className={`add_answer_confirm`} onClick={onAddAnswer}>
              <i className={`${FontAwesomeConfigurations.ICON_CHECK} fa-fw`} />
            </a>
            <a className={`add_answer_cancel`} onClick={onAddAnswerCancel}>
              <i className={`${FontAwesomeConfigurations.ICON_XMARK} fa-fw`} />
            </a>
          </div>
        </div>
      )}
    </>
  ) : (
    <></>
  );
}
